import { supabase } from '@/lib/supabase';

export type TimeRange = '1d' | '7d' | '30d' | '90d' | 'all';

export interface ItemAdditionDataPoint {
  date: string;
  count: number;
}

export interface UserAdditionStats {
  user: string;
  count: number;
}

export interface CategoryStats {
  name: string;
  count: number;
}

export async function getItemAdditionStats(timeRange: TimeRange): Promise<ItemAdditionDataPoint[]> {
  try {
    // Define the start date based on the selected time range
    let startDate: Date | null = new Date();
    
    if (timeRange === '1d') {
      startDate.setDate(startDate.getDate() - 1);
    } else if (timeRange === '7d') {
      startDate.setDate(startDate.getDate() - 7);
    } else if (timeRange === '30d') {
      startDate.setDate(startDate.getDate() - 30);
    } else if (timeRange === '90d') {
      startDate.setDate(startDate.getDate() - 90);
    } else if (timeRange === 'all') {
      startDate = null;
    }

    let query = supabase
      .from('db')
      .select('created_at');
    
    if (startDate) {
      query = query.gte('created_at', startDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching item addition stats:', error);
      throw error;
    }

    // Group the data by date
    const groupedByDate = data?.reduce<Record<string, number>>((acc, item) => {
      // Format the date as YYYY-MM-DD
      const date = new Date(item.created_at).toISOString().split('T')[0];
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {}) || {};

    // Fill in missing dates with zero counts
    const result: ItemAdditionDataPoint[] = [];
    
    if (timeRange !== 'all' && startDate) {
      const endDate = new Date();
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        result.push({
          date: dateStr,
          count: groupedByDate[dateStr] || 0
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else {
      // For 'all' just use the available data points
      Object.entries(groupedByDate).forEach(([date, count]) => {
        result.push({ date, count });
      });
      
      // Sort by date
      result.sort((a, b) => a.date.localeCompare(b.date));
    }

    return result;
  } catch (error) {
    console.error('Error in getItemAdditionStats:', error);
    throw error;
  }
}

export async function getUserAdditionStats(timeRange: TimeRange): Promise<UserAdditionStats[]> {
  try {
    // Define the start date based on the selected time range
    let startDate: Date | null = new Date();
    
    if (timeRange === '1d') {
      startDate.setDate(startDate.getDate() - 1);
    } else if (timeRange === '7d') {
      startDate.setDate(startDate.getDate() - 7);
    } else if (timeRange === '30d') {
      startDate.setDate(startDate.getDate() - 30);
    } else if (timeRange === '90d') {
      startDate.setDate(startDate.getDate() - 90);
    } else if (timeRange === 'all') {
      startDate = null;
    }

    let query = supabase
      .from('db')
      .select('created_by');
    
    if (startDate) {
      query = query.gte('created_at', startDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching user addition stats:', error);
      throw error;
    }

    // Group the data by user
    const groupedByUser = data?.reduce<Record<string, number>>((acc, item) => {
      const user = item.created_by || 'Unknown user';
      acc[user] = (acc[user] || 0) + 1;
      return acc;
    }, {}) || {};

    // Convert to array and sort by count (descending)
    return Object.entries(groupedByUser)
      .map(([user, count]) => ({ user, count }))
      .sort((a, b) => b.count - a.count);
  } catch (error) {
    console.error('Error in getUserAdditionStats:', error);
    throw error;
  }
}

export async function getCategoryStats(timeRange: TimeRange): Promise<CategoryStats[]> {
  try {
    let query = supabase.from('db').select('*');
    
    if (timeRange !== 'all') {
      // Define the start date based on the selected time range
      let startDate = new Date();
      
      if (timeRange === '1d') {
        startDate.setDate(startDate.getDate() - 1);
      } else if (timeRange === '7d') {
        startDate.setDate(startDate.getDate() - 7);
      } else if (timeRange === '30d') {
        startDate.setDate(startDate.getDate() - 30);
      } else if (timeRange === '90d') {
        startDate.setDate(startDate.getDate() - 90);
      }
      
      query = query.gte('created_at', startDate.toISOString());
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching category stats:', error);
      throw new Error('Failed to fetch category statistics');
    }
    
    const categoryMap = new Map<string, number>();
    
    data?.forEach(item => {
      const categories = [
        item['category[0]'],
        item['category[1]'],
        item['category[2]']
      ];
      
      categories.forEach(category => {
        if (category && typeof category === 'string' && category.trim() !== '') {
          if (categoryMap.has(category)) {
            categoryMap.set(category, categoryMap.get(category)! + 1);
          } else {
            categoryMap.set(category, 1);
          }
        }
      });
    });
    
    return Array.from(categoryMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Return top 10 categories
    
  } catch (error) {
    console.error('Error in getCategoryStats:', error);
    throw error;
  }
}
